import * as React from 'react';
import {
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Chip,
  Tooltip,
  Collapse,
  Link,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import SyncIcon from '@material-ui/icons/Sync';
import AddIcon from '@material-ui/icons/Add';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { InfoCard } from '@backstage/core-components';
import type { DevxpApi } from '../api';
import type { GithubSyncConfig } from '../types';

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  formRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  textField: {
    flex: 1,
    minWidth: 200,
  },
  privateKeyField: {
    width: '100%',
  },
  statusMessage: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
  },
  success: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  error: {
    backgroundColor: '#ffebee',
    color: '#c62828',
  },
  activeChip: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  inactiveChip: {
    backgroundColor: '#f5f5f5',
    color: '#757575',
  },
  guideBox: {
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  guideHeader: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    userSelect: 'none',
  },
  syncingRow: {
    opacity: 0.6,
  },
  lastSynced: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
}));

interface GithubSyncSectionProps {
  api: DevxpApi;
  onSyncComplete?: () => void;
}

export const GithubSyncSection = ({ api, onSyncComplete }: GithubSyncSectionProps) => {
  const classes = useStyles();
  const [configs, setConfigs] = React.useState<GithubSyncConfig[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [showGuide, setShowGuide] = React.useState(false);
  const [syncingIds, setSyncingIds] = React.useState<Set<number>>(new Set());
  const [statusMessage, setStatusMessage] = React.useState<{ text: string; isError: boolean } | null>(null);

  // Form state
  const [orgName, setOrgName] = React.useState('');
  const [githubHostname, setGithubHostname] = React.useState('github.com');
  const [appClientId, setAppClientId] = React.useState('');
  const [appPrivateKey, setAppPrivateKey] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const loadConfigs = React.useCallback(async () => {
    try {
      setLoading(true);
      const result = await api.getGithubSyncConfigs();
      setConfigs(result.configs);
    } catch {
      // failed silently
    } finally {
      setLoading(false);
    }
  }, [api]);

  React.useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const handleCreate = async () => {
    if (!orgName.trim() || !appClientId.trim() || !appPrivateKey.trim()) return;
    setSubmitting(true);
    setStatusMessage(null);
    try {
      const hostname = githubHostname.trim() || 'github.com';
      await api.createGithubSyncConfig(orgName.trim(), hostname, appClientId.trim(), appPrivateKey.trim());
      setStatusMessage({ text: `GitHub sync registered for org "${orgName}" on "${hostname}"`, isError: false });
      setOrgName('');
      setGithubHostname('github.com');
      setAppClientId('');
      setAppPrivateKey('');
      setShowForm(false);
      await loadConfigs();
    } catch (e: any) {
      setStatusMessage({ text: e.message || 'Failed to register sync config', isError: true });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (config: GithubSyncConfig) => {
    try {
      await api.toggleGithubSyncConfig(config.id, !config.active);
      await loadConfigs();
    } catch (e: any) {
      setStatusMessage({ text: e.message || 'Failed to update config', isError: true });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.deleteGithubSyncConfig(id);
      await loadConfigs();
    } catch (e: any) {
      setStatusMessage({ text: e.message || 'Failed to delete config', isError: true });
    }
  };

  const handleSync = async (id: number) => {
    setSyncingIds(prev => new Set(prev).add(id));
    setStatusMessage(null);
    try {
      const result = await api.syncGithubConfig(id);
      if (result.error) {
        setStatusMessage({ text: result.error, isError: true });
      } else {
        const repoNote = result.repoCount > 0 ? ` and ${result.repoCount} repositories` : '';
        setStatusMessage({
          text: `Synced ${result.count} members${repoNote} from "${result.orgName}"`,
          isError: false,
        });
        await loadConfigs();
        onSyncComplete?.();
      }
    } catch (e: any) {
      setStatusMessage({ text: e.message || 'Sync failed', isError: true });
    } finally {
      setSyncingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // Preconfigured GitHub App creation URL with required permissions
  const githubAppCreateUrl =
    'https://github.com/settings/apps/new?' +
    'name=devxp-member-sync&' +
    'url=https%3A%2F%2Fdevxp.net&' +
    'public=false&' +
    'members=read&' +
    'metadata=read';

  return (
    <InfoCard title="GitHub Organization Auto-Sync">
      {/* Setup Guide */}
      <Box
        className={classes.guideBox}
        onClick={() => setShowGuide(v => !v)}
      >
        <Box className={classes.guideHeader}>
          {showGuide ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          <Typography variant="body2" style={{ marginLeft: 4, fontWeight: 600 }}>
            How to set up a GitHub App for member and repository sync
          </Typography>
        </Box>
        <Collapse in={showGuide}>
          <Box mt={1}>
            <Typography variant="body2" paragraph>
              DevXP syncs GitHub organization members and repositories automatically using a{' '}
              <Link href="https://docs.github.com/en/apps/creating-github-apps/about-creating-github-apps/about-creating-github-apps" target="_blank" rel="noopener noreferrer">
                GitHub App
              </Link>
              . The app needs <strong>Read-only</strong> access to <strong>Organization members</strong> and{' '}
              <strong>Repository → Metadata</strong>.
            </Typography>
            <Typography variant="body2" component="div">
              <strong>Steps:</strong>
              <ol>
                <li>
                  <Link href={githubAppCreateUrl} target="_blank" rel="noopener noreferrer">
                    Click here to pre-configure and create your GitHub App
                  </Link>
                  {' '}— this link pre-fills the name and required permissions.
                </li>
                <li>
                  On the creation page, verify <em>Organization permissions</em> has{' '}
                  <strong>Members → Read-only</strong> and <em>Repository permissions</em> has{' '}
                  <strong>Metadata → Read-only</strong>.
                </li>
                <li>
                  After creating the app, note down the <strong>Client ID</strong> from the app's{' '}
                  <em>General</em> tab.
                </li>
                <li>
                  Scroll to <em>Private keys</em> and click <strong>Generate a private key</strong>.
                  Download the <code>.pem</code> file and paste its full contents below.
                </li>
                <li>
                  <Link href="https://docs.github.com/en/apps/using-github-apps/installing-your-own-github-app" target="_blank" rel="noopener noreferrer">
                    Install the app
                  </Link>
                  {' '}in your GitHub organization (Settings → Developer settings → GitHub Apps → Install).
                </li>
              </ol>
            </Typography>
            <Typography variant="body2">
              <Link href="https://docs.github.com/en/apps/creating-github-apps/registering-a-github-app/registering-a-github-app" target="_blank" rel="noopener noreferrer">
                Full GitHub App documentation →
              </Link>
            </Typography>
          </Box>
        </Collapse>
      </Box>

      {/* Status message */}
      {statusMessage && (
        <Box
          className={`${classes.statusMessage} ${statusMessage.isError ? classes.error : classes.success}`}
          mb={2}
        >
          <Typography variant="body2">{statusMessage.text}</Typography>
        </Box>
      )}

      {/* Add new config button / form */}
      {!showForm ? (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => { setShowForm(true); setStatusMessage(null); }}
          size="small"
        >
          Register GitHub App
        </Button>
      ) : (
        <Box className={classes.form}>
          <Typography variant="subtitle2">Register new GitHub App sync</Typography>
          <Box className={classes.formRow}>
            <TextField
              label="GitHub Org Name"
              value={orgName}
              onChange={e => setOrgName(e.target.value)}
              variant="outlined"
              size="small"
              className={classes.textField}
              placeholder="my-organization"
              helperText="Your GitHub organization's login name"
            />
            <TextField
              label="GitHub Hostname"
              value={githubHostname}
              onChange={e => setGithubHostname(e.target.value)}
              variant="outlined"
              size="small"
              className={classes.textField}
              placeholder="github.com"
              helperText='Use "github.com" or your GitHub Enterprise Server hostname (e.g. github.acme.com)'
            />
          </Box>
          <Box className={classes.formRow}>
            <TextField
              label="App Client ID"
              value={appClientId}
              onChange={e => setAppClientId(e.target.value)}
              variant="outlined"
              size="small"
              className={classes.textField}
              placeholder="Iv1.a1b2c3d4e5f67890"
              helperText="Found on the GitHub App's General tab"
            />
          </Box>
          <TextField
            label="App Private Key (PEM)"
            value={appPrivateKey}
            onChange={e => setAppPrivateKey(e.target.value)}
            variant="outlined"
            size="small"
            multiline
            rows={6}
            className={classes.privateKeyField}
            placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;...&#10;-----END RSA PRIVATE KEY-----"
            helperText="Paste the full contents of the .pem file downloaded from GitHub"
          />
          <Box style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreate}
              disabled={submitting || !orgName.trim() || !appClientId.trim() || !appPrivateKey.trim()}
            >
              {submitting ? 'Registering...' : 'Register'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => { setShowForm(false); setOrgName(''); setGithubHostname('github.com'); setAppClientId(''); setAppPrivateKey(''); }}
              disabled={submitting}
              style={{ marginLeft: 8 }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}



      {/* Configs table */}
      <Box mt={3}>
        {loading ? (
          <Typography variant="body2" color="textSecondary">Loading configurations...</Typography>
        ) : configs.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2">
              No GitHub sync configurations yet. Register a GitHub App above to enable automatic member syncing.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Organization</TableCell>
                  <TableCell>GitHub Host</TableCell>
                  <TableCell>Client ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Synced</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.map(cfg => {
                  const isSyncing = syncingIds.has(cfg.id);
                  return (
                    <TableRow key={cfg.id} className={isSyncing ? classes.syncingRow : undefined}>
                      <TableCell>
                        <strong>{cfg.org_name}</strong>
                      </TableCell>
                      <TableCell style={{ fontSize: '0.85em' }}>
                        {cfg.github_hostname ?? 'github.com'}
                      </TableCell>
                      <TableCell style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                        {cfg.app_client_id}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cfg.active ? 'Active' : 'Inactive'}
                          size="small"
                          className={cfg.active ? classes.activeChip : classes.inactiveChip}
                          onClick={() => handleToggle(cfg)}
                          clickable
                        />
                      </TableCell>
                      <TableCell>
                        {cfg.last_synced_at ? (
                          <Typography className={classes.lastSynced}>
                            {new Date(cfg.last_synced_at).toLocaleString()}
                          </Typography>
                        ) : (
                          <Typography className={classes.lastSynced}>Never</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Sync now">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleSync(cfg.id)}
                              disabled={isSyncing}
                              aria-label="Sync now"
                            >
                              <SyncIcon
                                fontSize="small"
                                style={isSyncing ? { animation: 'spin 1s linear infinite' } : undefined}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete configuration">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(cfg.id)}
                            disabled={isSyncing}
                            aria-label="Delete config"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </InfoCard>
  );
};
